import { Divider, Stack } from '@mui/material';
import { z } from 'zod';

import { api } from 'common';

import { OrderJobParamsFormFields, OrderJobParamsSchema } from './Common/Orders';
import { OrderProcedures } from './OrderProcedures';
import {
  type AsynchronousJobVariant,
  NullableStringSchema,
  OptionalStringSchema,
  RequiredStringSchema,
} from './common';
import { BlockValueTemplateField } from '../../../ConfigFields/CodeMirror/FlowTemplate';

import JobKind = api.RpaJob.Kind;

export const ExportAuthorizationSubmissionVariant: AsynchronousJobVariant = {
  schema: z.object({
    jobKind: z.literal(JobKind.ExportAuthorizationSubmission),
    payload: OrderJobParamsSchema.merge(
      z.object({
        payor: OptionalStringSchema,
        payorPlan: OptionalStringSchema,
        specialty: OptionalStringSchema,
        patient: z.object({
          firstName: RequiredStringSchema,
          lastName: RequiredStringSchema,
          dateOfBirth: RequiredStringSchema,
        }),
        insurance: z
          .object({
            memberId: OptionalStringSchema,
          })
          .optional()
          .nullable(),
        submission: z.object({
          cptCode: RequiredStringSchema,
          procedure: OptionalStringSchema,
          icd10Codes: RequiredStringSchema,
          siteOfService: z
            .object({
              name: OptionalStringSchema,
              npi: OptionalStringSchema,
              zipcode: OptionalStringSchema,
              address: OptionalStringSchema,
            })
            .optional()
            .nullable(),
          procedures: z.union([
            z
              .array(
                z.object({
                  id: RequiredStringSchema,
                  description: RequiredStringSchema,
                  cptCode: OptionalStringSchema,
                  areaOfInterest: OptionalStringSchema,
                  reasonForExam: OptionalStringSchema,
                  ehrReferenceValue: OptionalStringSchema,
                  rawDiagnoses: OptionalStringSchema,
                  standardProcedureId: NullableStringSchema,
                  patientOrderId: OptionalStringSchema,
                  diagnoses: z
                    .array(
                      z.object({
                        description: RequiredStringSchema,
                        icd10Codes: z.array(RequiredStringSchema).nullable().optional(),
                      })
                    )
                    .optional(),
                })
              )
              .optional(),
            OptionalStringSchema,
          ]),
          diagnoses: RequiredStringSchema,
          reasonForExam: OptionalStringSchema,
          referredByProvider: z.object({
            firstName: RequiredStringSchema,
            lastName: RequiredStringSchema,
            npi: RequiredStringSchema,
            phoneNumber: RequiredStringSchema,
          }),
          practice: z.object({
            taxId: RequiredStringSchema,
            phoneNumber: RequiredStringSchema,
            address: z.object({
              address: OptionalStringSchema,
              city: OptionalStringSchema,
              state: RequiredStringSchema,
              zip: OptionalStringSchema,
            }),
          }),
          serviceDate: RequiredStringSchema,
          referredByDepartment: OptionalStringSchema,
          referredToDepartment: OptionalStringSchema,
          patientPhoneNumber: OptionalStringSchema,
          department: z
            .object({
              contactName: OptionalStringSchema,
              name: OptionalStringSchema,
              fax: OptionalStringSchema,
              phone: OptionalStringSchema,
              npi: OptionalStringSchema,
              address: OptionalStringSchema,
            })
            .optional()
            .nullable(),
        }),
      })
    ),
  }),
  Config: () => {
    return (
      <Stack spacing={2}>
        {/* Payor Information */}
        <BlockValueTemplateField
          fieldName="config.payload.payor"
          label="Payor"
          helperText="Enter the reference to the payor."
          placeholder="e.g. {{order.data.payor}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.payorPlan"
          label="Payor Plan"
          helperText="Enter the reference to the payor plan."
          placeholder="e.g. {{order.data.payorPlan}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.specialty"
          label="Specialty"
          helperText="Enter the reference to the specialty."
          placeholder="e.g. {{order.data.specialty}}"
        />

        <Divider />

        {/* Patient Information */}
        <BlockValueTemplateField
          fieldName="config.payload.patient.firstName"
          label="Patient First Name"
          helperText="Enter the reference to the patient's first name."
          placeholder="e.g. {{patient.data.firstName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patient.lastName"
          label="Patient Last Name"
          helperText="Enter the reference to the patient's last name."
          placeholder="e.g. {{patient.data.lastName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patient.dateOfBirth"
          label="Patient Date of Birth"
          helperText="Enter the reference to the patient's date of birth."
          placeholder="e.g. {{patient.data.dateOfBirth}}"
        />

        <Divider />

        {/* Insurance Information */}
        <BlockValueTemplateField
          fieldName="config.payload.insurance.memberId"
          label="Insurance Member ID"
          helperText="Enter the reference to the insurance member ID."
          placeholder="e.g. {{order.data.insurance.memberId}}"
        />

        <Divider />

        {/* Site Of Service Information */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.siteOfService.npi"
          label="Site Of Service: NPI Number"
          helperText="Enter the reference to the NPI Number"
          placeholder="e.g. {{order.data.submission.siteOfService.npi}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.siteOfService.name"
          label="Site Of Service: Name"
          helperText="Enter the reference to the Site of Service Name"
          placeholder="e.g. {{order.data.submission.siteOfService.name}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.siteOfService.zipcode"
          label="Site Of Service: Zipcode"
          helperText="Enter the reference to the Site of Service Zipcode"
          placeholder="e.g. {{order.data.submission.siteOfService.zipcode}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.siteOfService.address"
          label="Site Of Service: Address"
          helperText="Enter the reference to the Site of Service Address"
          placeholder="e.g. {{order.data.submission.siteOfService.address}}"
        />

        <Divider />

        {/* Submission Details */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.cptCode"
          label="CPT Code"
          helperText="Enter the reference to the CPT Code."
          placeholder="e.g. {{order.data.submission.cptCode}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.procedure"
          label="Procedure"
          helperText="Enter the reference to the procedure."
          placeholder="e.g. {{order.data.submission.procedure}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.icd10Codes"
          label="ICD10 Codes"
          helperText="Enter the reference to the ICD10 Codes as an interpolated string."
          placeholder="e.g. {{order.data.submission.icd10Codes}}"
        />

        {/* Procedures */}
        <OrderProcedures prefix="config.payload.submission" />

        <Divider />

        {/* Diagnoses */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.diagnoses"
          label="Diagnoses"
          helperText="Enter the reference to the diagnoses as an interpolated string."
          placeholder="e.g. {{order.data.submission.diagnoses}}"
        />

        <Divider />

        {/* Reason for Exam */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.reasonForExam"
          label="Reason for Exam"
          helperText="Enter the reference to the reason for exam."
          placeholder="e.g. {{order.data.submission.reasonForExam}}"
        />

        <Divider />

        {/* Referred By Provider */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.referredByProvider.firstName"
          label="Referred By Provider First Name"
          helperText="Enter the reference to the referring provider's first name."
          placeholder="e.g. {{order.data.submission.referredByProvider.firstName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.referredByProvider.lastName"
          label="Referred By Provider Last Name"
          helperText="Enter the reference to the referring provider's last name."
          placeholder="e.g. {{order.data.submission.referredByProvider.lastName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.referredByProvider.npi"
          label="Referred By Provider NPI"
          helperText="Enter the reference to the referring provider's NPI."
          placeholder="e.g. {{order.data.submission.referredByProvider.npi}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.referredByProvider.phoneNumber"
          label="Referred By Provider Phone Number"
          helperText="Enter the reference to the referring provider's phone number."
          placeholder="e.g. {{order.data.submission.referredByProvider.phoneNumber}}"
        />

        <Divider />

        {/* Practice Information */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.practice.taxId"
          label="Practice Tax ID"
          helperText="Enter the reference to the practice's Tax ID."
          placeholder="e.g. {{order.data.submission.practice.taxId}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.practice.phoneNumber"
          label="Practice Phone Number"
          helperText="Enter the reference to the practice's phone number."
          placeholder="e.g. {{order.data.submission.practice.phoneNumber}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.practice.address.address"
          label="Practice Address"
          helperText="Enter the reference to the practice's address."
          placeholder="e.g. {{order.data.submission.practice.address.address}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.practice.address.city"
          label="Practice City"
          helperText="Enter the reference to the practice's city."
          placeholder="e.g. {{order.data.submission.practice.address.city}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.practice.address.state"
          label="Practice State"
          helperText="Enter the reference to the practice's state."
          placeholder="e.g. {{order.data.submission.practice.address.state}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.practice.address.zip"
          label="Practice ZIP Code"
          helperText="Enter the reference to the practice's ZIP code."
          placeholder="e.g. {{order.data.submission.practice.address.zip}}"
        />

        <Divider />

        {/* Service Date */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.serviceDate"
          label="Service Date"
          helperText="Enter the reference to the service date."
          placeholder="e.g. {{order.data.submission.serviceDate}}"
        />

        {/* Departments */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.referredByDepartment"
          label="Referred By Department"
          helperText="Enter the reference to the referring department."
          placeholder="e.g. {{order.data.submission.referredByDepartment}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.referredToDepartment"
          label="Referred To Department"
          helperText="Enter the reference to the department being referred to."
          placeholder="e.g. {{order.data.submission.referredToDepartment}}"
        />

        <Divider />

        {/* Patient Phone Number */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.patientPhoneNumber"
          label="Patient Phone Number"
          helperText="Enter the reference to the patient's phone number."
          placeholder="e.g. {{order.data.submission.patientPhoneNumber}}"
        />

        <Divider />

        {/* Order Job Params */}
        <OrderJobParamsFormFields />

        {/* Department Address */}
        <Divider />

        <BlockValueTemplateField
          fieldName="config.payload.submission.department.contactName"
          label="Department Contact Name"
          helperText="Enter the reference to Department Contact Name e.g. `{{department.data.contactName}}`"
          placeholder="e.g. {{department.data.contactName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.department.name"
          label="Department Name"
          helperText="Enter the reference to Department Name e.g. `{{department.data.name}}`"
          placeholder="e.g. {{department.data.name}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.department.fax"
          label="Department Fax"
          helperText="Enter the reference to Department Fax e.g. `{{department.data.fax}}`"
          placeholder="e.g. {{department.data.fax}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.department.phone"
          label="Department Phone"
          helperText="Enter the reference to Department Phone e.g. `{{department.data.phone}}`"
          placeholder="e.g. {{department.data.phone}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.department.npi"
          label="Department NPI"
          helperText="Enter the reference to Department NPI e.g. `{{department.data.npi}}`"
          placeholder="e.g. {{department.data.npi}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.department.address"
          label="Department Address"
          helperText="Enter the reference to Department Address e.g. `{{department.data.address}}`"
          placeholder="e.g. {{department.data.address}}"
        />
      </Stack>
    );
  },
};
